"use client"

import React from 'react';
import styles from './SharePopup.module.css';
import {
    FaFacebook,
    FaLinkedin,
    FaTimes,
    FaCopy,
    FaEnvelope,
    FaWhatsapp,
    FaQuoteRight
} from 'react-icons/fa';
import { BsTwitterX } from "react-icons/bs";
import { useSnackbar } from '@/context/SnackbarContext';
import { createPortal } from 'react-dom';

interface SharePopupProps {
    isOpen: boolean;
    onClose: () => void;
    shareUrl: string;
    title: string;
    citation?: string;
}

const SharePopup: React.FC<SharePopupProps> = ({ isOpen, onClose, shareUrl, title, citation }) => {
    const { showSnackbar } = useSnackbar();
    const [mounted, setMounted] = React.useState(false);
    const [isCitationOpen, setIsCitationOpen] = React.useState(false);
    const [copiedAll, setCopiedAll] = React.useState(false);

    React.useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    // Scroll freeze
    React.useEffect(() => {
        if (isOpen && mounted) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, mounted]);

    if (!isOpen || !mounted) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleCitationBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            setIsCitationOpen(false);
        }
    };

    const pageUrl = mounted ? window.location.href : '';

    const shareLinks = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`,
        xTwitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(title)}`,
        email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent("Check out this report: " + pageUrl)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + pageUrl)}`
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            showSnackbar('Link copied to clipboard!', 'success');
        } catch (err) {
            console.error('Failed to copy: ', err);
            showSnackbar('Failed to copy link', 'error');
        }
    };

    // Construct citations
    const currentYear = new Date().getFullYear();
    const apaCitation = citation || `The Business Research Company. (${currentYear}). ${title} Image. Retrieved from ${pageUrl}`;
    const mlaCitation = citation ? `"${title} Image." The Business Research Company, ${currentYear}, ${pageUrl}.` : `"${title} Image." The Business Research Company, ${currentYear}, ${pageUrl}.`;
    const chicagoCitation = citation ? `The Business Research Company. "${title} Image." Last modified ${currentYear}. ${pageUrl}.` : `The Business Research Company. "${title} Image." Last modified ${currentYear}. ${pageUrl}.`;

    const copyAllCitations = async () => {
        const allCitations = `APA:\n${apaCitation}\n\nMLA:\n${mlaCitation}\n\nChicago:\n${chicagoCitation}`;
        try {
            await navigator.clipboard.writeText(allCitations);
            setCopiedAll(true);
            setTimeout(() => setCopiedAll(false), 3000);
        } catch (err) {
            console.error('Failed to copy all: ', err);
        }
    };

    const modalContent = (
        <>
            <div className={styles.overlay} onClick={handleBackdropClick}>
                <div className={styles.modal}>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <FaTimes />
                    </button>

                    <h2 className={styles.title}>Share Content</h2>

                    <div className={styles.socialGrid}>
                        <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className={`${styles.iconLink} ${styles.facebook}`} title="Share on Facebook">
                            <FaFacebook />
                        </a>
                        <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className={`${styles.iconLink} ${styles.linkedin}`} title="Share on LinkedIn">
                            <FaLinkedin />
                        </a>
                        <a href={shareLinks.xTwitter} target="_blank" rel="noopener noreferrer" className={`${styles.iconLink} ${styles.xTwitter}`} title="Share on X">
                            <BsTwitterX />
                        </a>
                        <a href={shareLinks.email} className={`${styles.iconLink} ${styles.email}`} title="Share via Email">
                            <FaEnvelope />
                        </a>
                        <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className={`${styles.iconLink} ${styles.whatsapp}`} title="Share on WhatsApp">
                            <FaWhatsapp />
                        </a>
                    </div>

                    <div className={styles.actions}>
                        <button className={styles.actionBtn} onClick={copyToClipboard}>
                            <FaCopy className={styles.actionIcon} /> Copy Link
                        </button>
                        <button className={styles.actionBtn} onClick={() => setIsCitationOpen(true)}>
                            <FaQuoteRight className={styles.actionIcon} /> Add Citation
                        </button>
                    </div>
                </div>
            </div>

            {isCitationOpen && (
                <div className={styles.overlay} onClick={handleCitationBackdropClick} style={{ zIndex: 10000 }}>
                    <div className={styles.citationModal}>
                        <button className={styles.closeBtn} onClick={() => setIsCitationOpen(false)}>
                            <FaTimes />
                        </button>

                        <h2 className={styles.citationTitle}>Cite This Image</h2>
                        <div className={styles.divider}></div>

                        <div className={styles.citationBody}>
                            <div className={styles.citationBlock}>
                                <div className={styles.citationFormat}>APA:</div>
                                <div className={styles.citationText}>{apaCitation}</div>
                            </div>

                            <div className={styles.citationBlock}>
                                <div className={styles.citationFormat}>MLA:</div>
                                <div className={styles.citationText}>{mlaCitation}</div>
                            </div>

                            <div className={styles.citationBlock}>
                                <div className={styles.citationFormat}>Chicago:</div>
                                <div className={styles.citationText}>{chicagoCitation}</div>
                            </div>
                        </div>

                        <div className={styles.citationActions}>
                            <button className={styles.copyAllBtn} onClick={copyAllCitations}>
                                Copy All
                            </button>
                            {copiedAll && <span className={styles.copiedMessage}>All citations copied!</span>}
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return createPortal(modalContent, document.body);
};

export default SharePopup;
